import { ReactNode } from 'react'
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  Theme,
  StyledEngineProvider
} from '@mui/material/styles'
import MTTMilanoRegularWoff2 from 'src/fonts/MTTMilano-Regular.woff2'
import { colors, corporate, fonts } from './globalVars'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line
  interface DefaultTheme extends Theme {}
}

interface Alert {
  default: Record<string, any>
  warning: Record<string, any>
  info: Record<string, any>
  error: Record<string, any>
}
declare module '@mui/material/styles' {
  interface Theme {
    colors: Record<string, any>
    corporate: Record<string, any>
    fonts: Record<string, any>
    alert: Alert
  }
  interface ThemeOptions {
    colors?: Record<string, string>
    corporate?: Record<string, string>
    fonts?: Record<string, string>
    alert?: Alert
  }
}

const mttMilanoRegular = {
  fontFamily: 'Mtt-Milano',
  fontStyle: 'normal',
  fontWeight: 400,
  src: `
    local('Mtt-Milano'),
    local('Mtt-Milano'),
    url(${MTTMilanoRegularWoff2}) format('woff2')
  `,
  unicodeRange:
    'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF'
}

export const theme = createTheme({
  colors,
  corporate,
  fonts,
  palette: {
    primary: {
      main: corporate.primaryColor
    },
    secondary: {
      main: corporate.secondaryColor
    },
    success: {
      light: colors.green10,
      main: corporate.successColor
    },
    warning: {
      light: colors.orange04,
      main: corporate.warningColor
    },
    error: {
      main: corporate.errorColor,
      light: colors.red07
    },
    action: {
      selected: corporate.primaryColor
    }
  },
  shape: {
    borderRadius: 0
  },
  typography: {
    h1: {
      fontFamily: corporate.primaryFont
    },
    h2: {
      fontFamily: corporate.primaryFont
    },
    h3: {
      fontFamily: corporate.primaryFont
    },
    h4: {
      fontFamily: corporate.primaryFont,
      fontSize: '1.75rem'
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          '& .MuiCalendarPicker-root': {
            color: corporate.primaryColor,
            '& .MuiIconButton-root': {
              color: corporate.primaryColor,
              '&.Mui-disabled': {
                color: colors.gray04
              }
            }
          }
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        root: {
          FontFace: [mttMilanoRegular],
          '*::-webkit-scrollbar': {
            width: '0.4em'
          },
          '*::-webkit-scrollbar-track': {
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            outline: '1px solid slatetheme'
          }
        },
        body: {
          fontFamily: corporate.primaryFont,
          margin: 0,
          padding: 0,
          overflow: 'hidden',

          scrollbarColor: colors.main300,
          scrollbarWidth: 'thin',

          '*::-webkit-scrollbar': {
            WebkitAppearance: 'none',
            width: '6px',
            height: '6px',
            borderRadius: '8px'
          },

          '*::-webkit-scrollbar-thumb': {
            backgroundColor: '#B2BAD6',
            borderRadius: '8px'
          },

          '*::-webkit-scrollbar-track': {
            backgroundColor: '#E5E8F1'
          }
        },
        a: {
          color: 'inherit',
          textDecoration: 'none'
        }
      }
    },
    // MuiDialogActions: {
    //   styleOverrides: {
    //     root: {
    //       padding: '1rem',

    //       '& .primary-button .Mui-disabled': {
    //         color: colors.gray16,
    //         borderColor: colors.blue04,
    //         borderWidth: '1px'
    //       }
    //     }
    //   }
    // },
    MuiButton: {
      styleOverrides: {
        root: {
          color: corporate.primaryColor,
          height: '40px',
          fontFamily: corporate.primaryFont,
          fontSize: '16px',
          padding: '8px 12px',
          marginLeft: '12px',
          lineHeight: '20px',
          fontWeight: 700,
          textTransform: 'none',

          '&:hover': {
            backgroundColor: colors.blue08
          },

          '&:focus-visible': {
            backgroundColor: colors.blue07,
            boxShadow: '0px 2px 9px 0px rgba(113, 113, 113, 0.25)'
          }
        }
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            backgroundColor: corporate.secondaryColor,
            borderWidth: 1,
            borderRadius: '4px',
            borderStyle: 'solid',
            borderColor: corporate.primaryColor,

            '&:hover': {
              backgroundColor: colors.blue08,
              borderWidth: 1
            },

            '&:focus-visible': {
              borderWidth: 1,
              backgroundColor: colors.blue07,
              boxShadow: '0px 2px 9px 0px rgba(113, 113, 113, 0.25)'
            }
          }
        },
        {
          props: { variant: 'contained' },
          style: {
            backgroundColor: corporate.primaryColor,
            color: corporate.secondaryColor,
            borderRadius: '4px',

            '&:hover': {
              backgroundColor: colors.blue02
            },

            '&:focus-visible': {
              backgroundColor: colors.main900,
              boxShadow: '0px 2px 9px 0px rgba(113, 113, 113, 0.25)'
            }
          }
        }
      ]
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          background: corporate.secondaryColor
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',

          height: '30px',
          padding: '8px 8px 8px 16px',
          margin: '24px 24px 4px 24px',
          boxSizing: 'content-box',

          fontFamily: corporate.primaryFont,
          fontSize: '18px',
          fontWeight: 700,
          color: colors.white01,

          borderRadius: '4px',
          backgroundColor: corporate.primaryColor
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: '0',
          borderRadius: '8px'
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: corporate.primaryFont
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          // padding: 0
        }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '0 24px 24px 24px'
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: corporate.primaryFont,
          color: colors.gray14,
          fontSize: '16px',
          paddingLeft: '0',

          '&.required:after': {
            position: 'absolute',
            top: 0,
            content: '"*"',
            fontSize: '18px',
            color: corporate.errorColor,
            marginLeft: '3px'
          },

          '&.required.disabled:after': {
            // color: corporate.disabledColor
          },

          '&.disabled': {
            // color: corporate.disabledColor
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: '40px',
          fontFamily: corporate.secondaryFont,
          fontSize: '14px',
          color: colors.gray01,
          padding: '0 10px',
          '& fieldset': {
            border: 0
          }
        },
        formControl: {
          display: 'flex',
          height: '40px',
          flexGrow: 1,
          borderWidth: 1,
          borderColor: colors.gray03,
          borderStyle: 'solid',

          '&.error': {
            // backgroundColor: colors.red07,
            borderColor: corporate.errorColor
          },

          '&:not(.Mui-disabled):hover': {
            backgroundColor: corporate.color05,
            borderWidth: 1,
            borderColor: corporate.primaryColor
          },

          '& .Mui-focused': {
            backgroundColor: colors.white01,
            borderWidth: 1,
            borderColor: corporate.primaryColor
          }
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-input.MuiInputBase-input': {
            padding: 0
          }
        },
        popper: {
          borderRadius: '4px',
          background: colors.white01,
          boxShadow: '0px 2px 9px 0px rgba(113, 113, 113, 0.25)',
          marginTop: '2px !important',
          overflow: 'hidden'
        },
        listbox: {
          padding: '4px',
          margin: '0',
          boxShadow: 'none',

          li: {
            margin: '0',

            '&.MuiAutocomplete-option': {
              padding: '10px 4px',
              fontFamily: corporate.secondaryFont,
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '18px',
              color: colors.gray14
            },

            '&.MuiAutocomplete-option.Mui-focused': {
              backgroundColor: colors.blue07,
              color: corporate.primaryColor
            }
          }
        },
        popupIndicator: {
          '&:hover': {
            backgroundColor: 'transparent'
          }
        },
        clearIndicator: {
          '&:hover': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            // backgroundColor: corporate.primaryColorLight,
            borderWidth: 1,
            borderColor: corporate.primaryColor,

            '& .MuiSelect-outlined': {
              borderColor: corporate.primaryColor
            },

            '& .wrapperIcon': {
              borderColor: corporate.primaryColor,

              '& .MuiSvgIcon-root': {
                fill: corporate.primaryColor
              }
            }
          },
          '& .wrapperIcon': {
            position: 'absolute',
            right: '8px',
            top: '8px',
            width: '22px',
            height: '22px',
            paddingLeft: '8px',
            borderLeft: `1px solid ${colors.gray17}`,
            boxSizing: 'content-box',
            pointerEvents: 'none',

            '& .MuiSvgIcon-root': {
              right: 0,
              fontSize: '22px'
            }
          }
        }
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          fontFamily: corporate.primaryFontBold,
          color: corporate.primaryColor,
          '& .MuiTypography-root': {
            fontSize: '14px'
          }
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          minHeight: '50px',
          fontFamily: corporate.primaryFont,
          color: corporate.secondaryColor,
          borderRadius: '4px',
          padding: '6px 16px',

          '& .MuiAlert-icon': {
            color: corporate.secondaryColor,
            fontSize: '34px',
            opacity: 0.5,
            padding: 0
          },

          '& .MuiAlert-message': {
            opacity: 0.8
          },

          '&.MuiPaper-root': {
            '&.MuiAlert-standardError': {
              backgroundColor: corporate.errorColor,
              color: corporate.secondaryColor
            },
            '&.MuiAlert-standardSuccess': {
              backgroundColor: corporate.successColor,
              color: corporate.secondaryColor
            },
            '&.MuiAlert-standardInfo': {
              backgroundColor: corporate.warningInfo,
              color: colors.gray14
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: corporate.warningColor,
              color: colors.gray14
            }
          }
        },
        standardError: {
          backgroundColor: corporate.errorColor
        },
        standardSuccess: {
          backgroundColor: corporate.successColor
        },
        standardInfo: {
          backgroundColor: corporate.primaryColor
        },
        standardWarning: {
          backgroundColor: corporate.warningColor
        }
      }
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true
      },
      styleOverrides: {
        root: {
          position: 'relative',
          padding: 0,
          width: 38,
          height: 20,

          '& .Mui-checked': {
            '& .MuiSwitch-thumb': {
              left: '6px'
            }
          }
        },
        track: {
          width: '40px',
          height: '20px',
          backgroundColor: 'transparent',
          opacity: 1,
          border: `1px solid ${corporate.primaryColor}`,
          borderRadius: '20px',

          '.Mui-checked &': {
            backgroundColor: corporate.primaryColor
          },

          '.Mui-disabled &': {
            backgroundColor: colors.white01,
            borderColor: colors.gray17
          }
        },

        thumb: {
          width: '12px',
          height: '12px',
          position: 'relative',
          top: '-2px',
          left: '-2px',
          backgroundColor: corporate.primaryColor,

          '.Mui-checked &': {
            backgroundColor: '#EBEAEA'
          },

          '.Mui-disabled &': {
            backgroundColor: colors.gray17
          }
        },

        switchBase: {
          padding: 0,
          top: 6,
          left: 6,

          '&.Mui-checked ': {
            left: -3
          },

          '&:hover': {},

          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 1
          },

          '&.Mui-checked:hover': {},

          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 1
          },

          '&.Mui-checked.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: '#EBEAEA'
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            fontSize: '22px'
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          height: '22px',
          border: `1px solid ${colors.gray17}`,
          padding: 8,
          borderRadius: '4px',
          background: colors.white01,
          paddingRight: '44px',

          '&:hover': {
            backgroundColor: colors.blue08
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          maxWidth: '200px',

          '& .MuiTooltip-tooltip': {
            borderRadius: '4px',
            backgroundColor: colors.blue08,
            color: colors.gray14,
            fontFamily: corporate.primaryFont,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '18px',
            padding: '8px',
            boxShadow: '0px 1px 3px 0px rgba(113, 113, 113, 0.25)'
          },

          '& .MuiTooltip-arrow': {
            color: colors.blue08
          }
        }
      }
    }
  }
})

type Props = {
  children: ReactNode
}

export default function ThemeProvider({ children }: Props): JSX.Element {
  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </StyledEngineProvider>
  )
}
